import type { FolderOutput, FolderStatsV2, GetItemByIdentifierOutput } from '@core/api/generated'
import * as generated from '@core/api/generated'
import { EditorSettings } from '@core/components/editor/editor-v2/editor'
import { UserAndFolders } from '@core/folders/folders-api'
import type { Item } from '@pages/project-details/project-details-types'

import { FilterType } from './my-folders-state'

export const transformToEditorSettings = (
  settings: generated.EditorSettingsOutput,
): EditorSettings => {
  return {
    upload: settings?.imagesUpload || false,
    keyboards: settings?.math ? settings.math : [],
    wolfram: settings?.wolfram || false,
    desmos: settings?.desmos || false,
  }
}

/**
 * This function needs to exist until we invest time in refactoring the Item Preview dialog.
 * It converts the returned item from the BFF to the format used in the ItemViewModal (Hasura).
 */
export const adaptBffItem = (itemFromBff: GetItemByIdentifierOutput): Item => {
  const rootContentVersion = {
    ...itemFromBff.contentVersion,
    external_metadata: itemFromBff.contentVersion.externalMetadata,
    content: itemFromBff.contentVersion.content || {},
  }

  const children = itemFromBff.children.map((child) => {
    return {
      ...child.item,
      type: child.item.contentType,
      currentContent: [child.contentVersion],
      savedContent: [child.contentVersion],
    }
  })

  const modifiedItem = {
    ...itemFromBff.item,
    projectId: itemFromBff.folder?.id,
    modelInfo: itemFromBff.item.modelInfo,
    children,
    currentContent: [rootContentVersion],
    savedContent: [rootContentVersion],
    editorSettings: itemFromBff.editorSettings,
    itemModule: itemFromBff.itemModule,
  } as unknown as Item

  return modifiedItem
}

export type FolderWithParent = FolderOutput & {
  parent: string[]
  allChildrenIds: string[]
  childrenIds: string[]
}

// Recursively build a flat list of folders,
// and add parent id path to each folder like parent: [parent1, parent2, parent3]
// each folder should return all theirs children ids
export const getFlatFolderList = (
  folders: (FolderOutput | FolderWithParent)[],
  topLevelPermissions?: FolderOutput['permissions'],
): FolderWithParent[] => {
  return folders.flatMap((folder) => {
    const children = folder.children || []
    const parent = 'parent' in folder ? folder.parent : []
    const permissions = folder.permissions || topLevelPermissions

    // Collect all children IDs for this folder (direct children + their descendants)
    const allChildrenIds: string[] = []
    const childrenIds = folder.children.map((child) => child.id)

    const collectChildrenIds = (childFolders: FolderOutput[]) => {
      childFolders.forEach((child) => {
        allChildrenIds.push(child.id)

        if (child.children) {
          collectChildrenIds(child.children)
        }
      })
    }

    collectChildrenIds(children)

    return [
      { ...folder, parent, permissions, childrenIds, allChildrenIds },
      ...getFlatFolderList(
        children.map((child) => ({
          ...child,
          parent: [...parent, folder.id],
        })), // Pass children with updated parent path
        permissions,
      ),
    ]
  })
}

/**
 * Retrieves the path of a folder by its ID from a list of FolderOutput.
 * @param folders - The list of FolderOutput objects.
 * @param folderId - The ID of the folder to find.
 * @returns The path of the folder as a string, or null if not found.
 */
export const getFolderPathById = (folders: FolderOutput[], folderId: string): string => {
  const findFolderPath = (
    _folders: FolderOutput[],
    _folderId: string,
    parentPath: string = '',
  ): string => {
    for (const folder of _folders) {
      const currentPath = parentPath ? `${parentPath}/${folder.name}` : folder.name
      if (folder.id === _folderId) {
        return currentPath
      }
      if (folder.children && folder.children.length > 0) {
        const childPath = findFolderPath(folder.children, _folderId, currentPath)
        if (childPath) {
          return childPath
        }
      }
    }
    return ''
  }

  return findFolderPath(folders, folderId)
}

export const filterByUser = (
  users: string[],
  folderId: string,
  permissionsByUser?: Record<string, UserAndFolders>,
): boolean => {
  if (users.length === 0) return true
  return users.some((user) => {
    const userPermissions = permissionsByUser?.[user]
    if (!userPermissions) return false
    return userPermissions.folders.has(folderId)
  })
}

export const checkByType = (type: FilterType, stats: FolderStatsV2): boolean => {
  if (type === FilterType.all) return true
  if (type === 'internal') return Boolean(stats.internalItemsCount)
  if (type === 'clone') return Boolean(stats.clonesCount)
  return false
}

export const filterByDomains = (domains: string[], stats: FolderStatsV2): boolean => {
  if (domains.length === 0) return true
  return domains.some((domain) => Boolean(stats.domains.includes(domain)))
}

export const filterByTopics = (topics: string[], stats: FolderStatsV2): boolean => {
  if (topics.length === 0) return true
  return topics.some((topic) => Boolean(stats.topics.includes(topic)))
}

export const filterByProfiles = (profiles: string[], stats: FolderStatsV2): boolean => {
  if (profiles.length === 0) return true
  return profiles.some((profile) => Boolean(stats.profiles.includes(profile)))
}

export const filterFolderByName = (folderName: string, query: string): boolean => {
  if (!query) return true
  const lowerQuery = query.toLowerCase()
  return folderName.toLowerCase().includes(lowerQuery)
}
